/**
 * ReferralCard - Shows user's referral link + referral count.
 * Allows copying link or sharing via Web Share API.
 * @atomic @idempotent
 */
import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ShareIcon, UserPlusIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { toast } from '../../../services/toastService'
import { createLogger } from '../../../utils/logger'
import useReferral from '../../../hooks/useReferral'

const logger = createLogger('ReferralCard')

const ReferralCard = memo(function ReferralCard() {
  const { t } = useTranslation()
  const { referralLink, referralCount, referralCode, loading } = useReferral()

  const handleCopy = useCallback((e) => {
    e.preventDefault()
    if (!referralLink) return
    navigator.clipboard.writeText(referralLink).then(
      () => toast.success(t('referral.copied', 'Referral link copied!')),
      () => toast.error(t('referral.copyFailed', 'Could not copy link'))
    )
  }, [referralLink, t])

  const handleShare = useCallback((e) => {
    e.preventDefault()
    if (!referralLink) return
    if (navigator.share) {
      navigator.share({
        title: t('referral.shareTitle', 'Join me on TrckIt'),
        text: t('referral.shareText', 'Track food trucks & vendors in your city!'),
        url: referralLink,
      }).catch((err) => logger.warn('Share cancelled', err))
    } else {
      handleCopy(e)
    }
  }, [referralLink, handleCopy, t])

  if (loading) return null

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <UserPlusIcon className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-gray-800 text-sm">{t('referral.title', 'Invite Friends')}</h3>
        {referralCount > 0 && (
          <span className="ml-auto text-xs font-medium bg-primary/20 text-primary px-2 py-0.5 rounded-full">
            {t('referral.count', '{{count}} referred', { count: referralCount })}
          </span>
        )}
      </div>

      <p className="text-xs text-gray-600 mb-3">
        {t('referral.description', 'Share your link and help grow the TrckIt community!')}
      </p>

      {referralCode && (
        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2 mb-3">
          <span className="flex-1 text-xs font-mono text-gray-700 truncate">{referralLink}</span>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleCopy}
          onTouchEnd={handleCopy}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 px-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <ClipboardDocumentIcon className="w-4 h-4" />
          {t('referral.copy', 'Copy Link')}
        </button>
        <button
          onClick={handleShare}
          onTouchEnd={handleShare}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 px-3 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity"
        >
          <ShareIcon className="w-4 h-4" />
          {t('referral.share', 'Share')}
        </button>
      </div>
    </div>
  )
})

export default ReferralCard
